import React, { useCallback, useState } from 'react';
import {
  AngleInput,
  Composites,
  Divider,
  OpacityPicker,
  Slider,
  TextLabel,
  ToggleSwitch,
  ColorPickerInput,
} from '@wix/wix-base-ui';
import { ShadowSectionProps } from './TextMaskDesignPanel.types';
import trnKeys from '../panels/editor/TextMask.classic.translations.json';

const DEFAULT_SHADOW = {
  x: 1.41,
  y: 1.41,
  blurRadius: 2,
  color: '#000000',
  opacity: 0.6,
};

export const ShadowSection: React.FC<ShadowSectionProps> = ({
  shadow = DEFAULT_SHADOW,
  openColorPicker,
  colorResolver,
  updateDesignWithBI,
  translate,
  textLabelType = 'T02',
}) => {
  const localSavedAngle = React.useRef<number>(0);
  const { angle, distance } = getAngleDistance(shadow.x, shadow.y);
  const [lastShadow, setLastShadow] = useState(
    (shadow?.x && shadow?.y && shadow) || DEFAULT_SHADOW,
  );
  const isShadowApplied = Boolean(shadow && Object.keys(shadow).length > 0);

  /**
   * Get angle in deg and distance values from x,y values
   */
  function getAngleDistance(x: number = 0, y: number = 0) {
    const distanceInPx = Math.round(Math.sqrt(x * x + y * y) * 100) / 100;
    const angleInDeg = distanceInPx
      ? (Math.atan2(x, -y) * 180) / Math.PI
      : localSavedAngle.current;

    localSavedAngle.current = angleInDeg;

    return {
      angle: Math.round(((angleInDeg + 360) % 360) * 100) / 100,
      distance: distanceInPx,
    };
  }
  const handleShadowIsOnChange = (newValue: boolean) => {
    if (newValue) {
      updateDesignWithBI(
        { shadow: lastShadow },
        { fieldNames: 'shadow_apply', fieldValues: 'on' },
      );
    } else {
      setLastShadow(shadow);
      updateDesignWithBI(
        { shadow: {} },
        { fieldNames: 'shadow_apply', fieldValues: 'off' },
      );
    }
  };

  const handleAngleChange = useCallback(
    (angleInDeg: number) => {
      localSavedAngle.current = angleInDeg;
      const angleInRad = (((angleInDeg % 360) + 180) * Math.PI) / 180;
      const x = Math.round(distance * Math.sin(-angleInRad) * 100) / 100;
      const y = Math.round(distance * Math.cos(-angleInRad) * 100) / 100;

      updateDesignWithBI(
        { shadow: { ...shadow, x, y } },
        { fieldNames: 'shadow_angle', fieldValues: angleInDeg + '' },
      );
    },
    [distance, shadow, updateDesignWithBI],
  );

  const handleDistanceChange = (newValue: number) => {
    const angleInRad = (((angle % 360) + 180) * Math.PI) / 180;
    const x = Math.round(newValue * Math.sin(-angleInRad) * 100) / 100;
    const y = Math.round(newValue * Math.cos(-angleInRad) * 100) / 100;

    updateDesignWithBI(
      { shadow: { ...shadow, x, y } },
      { fieldNames: 'shadow_distance', fieldValues: newValue + '' },
    );
  };

  const handleBlurChange = (newValue: number) => {
    updateDesignWithBI(
      { shadow: { ...shadow, blurRadius: newValue } },
      { fieldNames: 'shadow_blur', fieldValues: newValue + '' },
    );
  };

  const handleOpacityChange = (newValue: number) => {
    updateDesignWithBI(
      { shadow: { ...shadow, opacity: newValue / 100 } },
      { fieldNames: 'shadow_opacity', fieldValues: newValue + '' },
    );
  };

  const handleColorChange: React.MouseEventHandler<HTMLElement> = (e) => {
    openColorPicker(
      shadow.color,
      handleColorPickerValueChange,
      e.currentTarget!.getBoundingClientRect(),
    );
  };

  const handleColorPickerValueChange = (newValue: string) => {
    updateDesignWithBI(
      { shadow: { ...shadow, color: newValue } },
      { fieldNames: 'shadow_color', fieldValues: newValue },
    );
  };

  return (
    <div className="section">
      <Composites.ToggleSwitch>
        <ToggleSwitch
          value={isShadowApplied}
          labelType={textLabelType}
          shouldTranslate={false}
          label={translate(trnKeys.section_shadow_enable)}
          onChange={handleShadowIsOnChange}
        />
      </Composites.ToggleSwitch>

      {isShadowApplied && (
        <div key="shadow-controls" className="shadow-controls">
          <Divider long={false} />
          <Composites.AngleInputLabeled>
            <TextLabel
              type={textLabelType}
              value={translate(trnKeys.section_shadow_angle)}
              shouldTranslate={false}
            />
            <AngleInput
              value={Math.round(angle)}
              onChange={handleAngleChange}
            />
          </Composites.AngleInputLabeled>

          <Divider long={false} />

          <Composites.SliderLabeled>
            <TextLabel
              type={textLabelType}
              value={translate(trnKeys.section_shadow_distance)}
              shouldTranslate={false}
            />
            <Slider
              value={Math.round(distance)}
              max={20}
              onChange={handleDistanceChange}
              unit="px"
            />
          </Composites.SliderLabeled>

          <Divider long={false} />

          <Composites.SliderLabeled>
            <TextLabel
              type={textLabelType}
              value={translate(trnKeys.section_shadow_blur)}
              shouldTranslate={false}
            />
            <Slider
              value={shadow.blurRadius}
              max={40}
              onChange={handleBlurChange}
              unit="px"
            />
          </Composites.SliderLabeled>

          <Divider long={false} />

          <Composites.ColorPickerInputLabeled automationId="mp_design_border_fill_color">
            <TextLabel
              type={textLabelType}
              value={translate(trnKeys.section_shadow_color_opacity)}
              shouldTranslate={false}
            />
            <Composites.ColorPickerInput>
              <OpacityPicker
                value={Math.round(shadow.opacity * 100)}
                onChange={handleOpacityChange}
                opacityGradientColor={colorResolver(shadow.color)}
              />
              <ColorPickerInput
                value={colorResolver(shadow.color)}
                opacity={100}
                onClick={handleColorChange}
              />
            </Composites.ColorPickerInput>
          </Composites.ColorPickerInputLabeled>
        </div>
      )}
    </div>
  );
};
